//score, time vs primary, secondary

import React from 'react'
import { Pressable, ScrollView, StyleSheet, View } from 'react-native'
import { GAME_CONSTANTS } from '@ppwr/convex/shared/constants'
import { DS } from '@/design-system'

import { formatMs } from '@/utils/timeFormatter'
import { formatChipsNumber } from '@/components/GameAssets/Player/PlayerBox'

import * as cth from '@/router/(root)/cth/cthModule'
import * as d10 from '@/router/(root)/daily10/d10Module'
import * as pocketPick from '@/router/(root)/pocketPick/pocketPickModule'

import { defaultTheme } from '@/components/Mini/theme'

const { DEFAULT_LEADERBOARD_CAP } = GAME_CONSTANTS

function getLeaderboardTheme(theme: typeof defaultTheme) {

  return {
    //card
    cardColor: theme.surface,
    cardBorderColor: theme.surfaceBorder,
    //header
    titleTextColor: theme.accent,
    arrowColor: theme.textDefault,
    //row
    rowBackgroundColor: theme.rowBackground,
    rankTextColor: theme.textDefaultDim,
    nameTextColor: theme.textDefault,
    primaryScoreTextColor: theme.accent,
    secondaryScoreTextColor: theme.textDefaultSlightDim,
    //highlighted row
    highlightedNameTextColor: theme.accent,
    highlightedRowBorderColor: theme.accent,
    //msg
    msgTextColor: theme.textDefaultDim,
    //divider
    dividerColor: theme.textDefaultDim
  }
}

type LeaderboardTheme = ReturnType<typeof getLeaderboardTheme>


type LeaderboardCardProps = {
  userId: string | null
  userEntry: { name: string; primaryScore: string; secondaryScore: string | number } | object
  status: { status: string } | null | undefined
  leaderboard:
    | Array<{ userId: string; name: string; primaryScore: string; secondaryScore: string | number }>
    | undefined
  handleLeaderboard: () => void
  theme: LeaderboardTheme
  compact?: boolean
}

function PocketPickLeaderboardCard({ compact = false }: { compact?: boolean }) {
  const { userId, userName } = pocketPick.usePocketPickUser()
  const { status } = pocketPick.usePocketPickQueryStatus()
  const { leaderboard } = pocketPick.usePocketPickQueryLeaderboard()
  const handleLeaderboard = pocketPick.useHandleLeaderboard()
  const ppTheme = pocketPick.useStore(s => s.theme)
  const theme = getLeaderboardTheme(ppTheme) 

  const ROUND_COUNT = pocketPick.useStore(s => s.ROUND_COUNT)

  function getScores(d: any) {
    return {
      primaryScore: d.roundsCorrect + '/' + ROUND_COUNT,
      secondaryScore: formatMs(d.totalElapsedMs)
    }
  }

  const leaderboardData = leaderboard?.map(function (d) {
    return { ...d, ...getScores(d) }
  })

  const userEntry = status?.status === 'completed' ? { name: userName, ...getScores(status) } : {}

  return (
    <LeaderboardCard
      userId={userId}
      userEntry={userEntry}
      status={status}
      leaderboard={leaderboardData}
      handleLeaderboard={handleLeaderboard}
      theme={theme}
      compact={compact}
    />
  )
}

function D10LeaderboardCard({ compact = false }: { compact?: boolean }) {
  const { userId, userName } = d10.useD10User()
  const { status } = d10.useD10QueryStatus()
  const { leaderboard } = d10.useD10QueryLeaderboard()
  const handleLeaderboard = d10.useHandleLeaderboard()

  const d10Theme = d10.useStore(s => s.theme)
  const theme = getLeaderboardTheme(d10Theme)

  function getScores(d: any) {
    return {
      primaryScore: formatChipsNumber(d.finalChips),
      secondaryScore: d.countedHands
    }
  }

  const leaderboardData = leaderboard?.map(function (d) {
    return { ...d, ...getScores(d) }
  })

  const userEntry = status?.status === 'completed' ? { name: userName, ...getScores(status?.result) } : {}

  return (
    <LeaderboardCard
      userId={userId}
      userEntry={userEntry}
      status={status}
      leaderboard={leaderboardData}
      handleLeaderboard={handleLeaderboard}
      theme={theme}
      compact={compact}
    />
  )
}

function CthLeaderboardCard({ compact = false }: { compact?: boolean }) {
  const { userId, userName } = cth.useCthUser()
  const { status } = cth.useCthQueryStatus()
  const { leaderboard } = cth.useCthQueryLeaderboard()
  const handleLeaderboard = cth.useHandleLeaderboard()

  const cthTheme = cth.useStore(s => s.theme)
  const theme = getLeaderboardTheme(cthTheme) 

  const ROUND_COUNT = cth.useStore(s => s.ROUND_COUNT)

  function getScores(d: any) {
    return {
      primaryScore: d.roundsCorrect + '/' + ROUND_COUNT,
      secondaryScore: formatMs(d.totalElapsedMs)
    }
  }

  const leaderboardData = leaderboard?.map(function (d) {
    return { ...d, ...getScores(d) }
  })

  const userEntry = status?.status === 'completed' ? { name: userName, ...getScores(status) } : {}

  return (
    <LeaderboardCard
      userId={userId}
      userEntry={userEntry}
      status={status}
      leaderboard={leaderboardData}
      handleLeaderboard={handleLeaderboard}
      theme={theme}
      compact={compact}
    />
  )
}

function LeaderboardCard({
  userId,
  userEntry,
  status,
  leaderboard,
  handleLeaderboard,
  theme,
  compact = false
}: LeaderboardCardProps) {
  const maxUsersToShow = compact === true ? 10 : DEFAULT_LEADERBOARD_CAP
  const leaderboardTopUsers = leaderboard?.slice(0, maxUsersToShow) ?? []

  const userIdx = leaderboardTopUsers?.findIndex((e) => e.userId === userId)

  const showUserNotInTopUsers = userIdx === -1 && status?.status === 'completed'

  const showCompletePuzzleMsg = status?.status !== 'completed'

  //const cardStyles = compact
    //? {...styles.card, ...{ backgroundColor: theme.cardColor, borderColor: theme.cardBorderColor }}
    //: styles.containerFull

  const cardStyles = compact
    ? {...styles.card, ...{ backgroundColor: theme.cardColor, borderColor: theme.cardBorderColor }}
    : {...styles.card, ...styles.containerFull, ...{ backgroundColor: theme.cardColor, borderColor: theme.cardBorderColor }}
  
  return (
    <View
      style={cardStyles}
    >
      {/*header*/}
      {compact === true && (
        <Pressable style={styles.header} onPress={handleLeaderboard}>
          <DS.Text variant="Heading 3/Medium" style={{ color: theme.titleTextColor, lineHeight: 30, textAlign: 'center' }}>
            Today's Leaderboard
          </DS.Text>

          <View style={{ position: 'absolute', right: 0 }}>
            <DS.Icon name="Arrow---right" size={20} color={theme.arrowColor} />
          </View>
        </Pressable>
      )}

      {/* no results yet*/}
      {leaderboardTopUsers?.length === 0 && <LeaderboardMsg theme={theme} msg="No results yet today" />}

      {/* some results */}
      {leaderboardTopUsers?.length > 0 && (
        <View style={{ maxHeight: '100%' }}>
          <ScrollView showsVerticalScrollIndicator={false} scrollEnabled={!compact}>
            {/* top users */}
            {leaderboardTopUsers?.map((entry, i) => {
              const isHighlighted = i === userIdx
              return (
                <LeaderboardItem key={entry.userId} theme={theme} entry={entry} isHighlighted={isHighlighted} i={i} />
              )
            })}

            {/* user if not in top users */}
            {compact == true && showUserNotInTopUsers && (
              <>
                <LeaderboardDivider theme={theme} />
                <LeaderboardItem entry={userEntry} theme={theme} isHighlighted={true} />
              </>
            )}
          </ScrollView>

          {/*user hasn't played yet*/}
          {compact === false && showCompletePuzzleMsg && (
            <LeaderboardMsg theme={theme} msg="Complete today's puzzle to join the leaderboard" />
          )}
        </View>
      )}
    </View>
  )
}

function LeaderboardMsg({ theme, msg }: { theme: LeaderboardTheme; msg: string }) {
  const msgColor = theme.msgTextColor

  return (
    <DS.Text style={[styles.leaderboardMsg, { color: msgColor }]} variant="Body Small/Regular" textAlign="center">
      {msg}
    </DS.Text>
  )
}

function LeaderboardItem({
  theme,
  entry,
  isHighlighted,
  i = undefined
}: { theme: LeaderboardTheme; entry: any; isHighlighted: boolean; i?: number }) {
  const defaultCellVariant = 'Body Medium/Regular'
  const rankColor = theme.rankTextColor
  const nameCellVariant = 'Body Medium/Medium'
  const nameColor = isHighlighted ? theme.highlightedNameTextColor : theme.nameTextColor
  const scoreColor = theme.primaryScoreTextColor
  const timeColor = theme.secondaryScoreTextColor
  const rowBackgroundColor = theme.rowBackgroundColor
  const highlightedRowBorderColor = theme.highlightedRowBorderColor
  const leaderboardRank = i !== undefined ? '#' + (i + 1) : ' '

  return (
    <View
      style={[
        styles.leaderboardRow,
        { backgroundColor: rowBackgroundColor },
        isHighlighted && { borderWidth: 1, borderColor: highlightedRowBorderColor }
      ]}
    >
      <DS.Text variant={defaultCellVariant} style={[styles.leaderboardRank, { color: rankColor }]}>
        {leaderboardRank}
      </DS.Text>
      <DS.Text variant={nameCellVariant} style={[styles.leaderboardName, { color: nameColor }]} numberOfLines={1}>
        {entry.name}
      </DS.Text>
      <DS.Text variant={defaultCellVariant} style={[styles.leaderboardScore, { color: scoreColor }]}>
        {entry.primaryScore}
      </DS.Text>
      <DS.Text variant={defaultCellVariant} style={[styles.leaderboardTime, { color: timeColor }]}>
        {entry.secondaryScore}
      </DS.Text>
    </View>
  )
}

function LeaderboardDivider({ theme }: { theme: LeaderboardTheme }) {
  const dividerColor = theme.dividerColor

  return (
    <View style={styles.leaderboardDivider}>
      <DS.Text variant="Body Small/Regular" textAlign="center" style={{ color: dividerColor }}>
        ...
      </DS.Text>
    </View>
  )
}

const styles = StyleSheet.create({
  containerFull: {
    //maxHeight: '100%',
    flexShrink:1, 
    minHeight: 0
  },
  card: {
    borderRadius: 20,
    padding: 20,
    borderWidth: 1,
    gap: 20,
    marginBottom: 16
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative'
  },
  leaderboardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 14,
    gap: 16,
    marginBottom: 8
  },
  leaderboardDivider: {
    paddingVertical: 4,
    marginBottom: 8
  },
  leaderboardRank: {
    width: 44
  },
  leaderboardName: {
    flex: 1
  },
  leaderboardScore: {
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },
  leaderboardTime: {
    textAlign: 'right',
    width: 44
  },
  leaderboardMsg: {
    padding: DS.spacing[8],
    alignItems: 'center'
  }
})
export { CthLeaderboardCard, D10LeaderboardCard, PocketPickLeaderboardCard }
export default LeaderboardCard
